import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Linkedin, MapPin, BookOpen, Cpu, Users, Layers, Infinity as InfinityIcon, Boxes, Webhook, LifeBuoy, Compass, Blocks, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
const BEN_IMAGE = '/other/Ben.webp';

// Icons per about.tags entry (same order as translations)
const tagIcons = [Layers, InfinityIcon, Boxes, Webhook, LifeBuoy, Compass, Blocks];

const About: React.FC = () => {
  const { t } = useLanguage();



  return (
    <div className="bg-maat-stone">
      <PageHeader
        tag={t.about.tag}
        title={t.about.title}
        titleAccent={t.about.titleAccent}
        subtitle={t.about.subtitle}
      />

      {/* Introduction */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-5xl border-4 border-white shadow-2xl">
                <img src={BEN_IMAGE} alt="Ben Degens" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-maat-gold text-maat-blue p-6 md:p-8 rounded-4xl shadow-xl block max-w-[170px] md:max-w-[200px]">
                <p className="font-serif font-bold text-lg leading-tight">{t.about.exp}</p>
              </div>
            </div>

            <div>
              <div className="space-y-6 text-lg text-maat-blue/80 leading-relaxed">
                <p>{t.about.p1}</p>
                <p>{t.about.p2}</p>
                <p>{t.about.p3}</p>
                <p>{t.about.p4}</p>
              </div>
              <div className="mt-10 flex flex-wrap gap-2.5">
                <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-xl border border-maat-sand text-maat-blue text-sm font-medium">
                  <MapPin size={16} className="text-maat-gold" />
                  <span>{t.about.location}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-xl border border-maat-sand text-maat-blue text-sm font-medium">
                  <MessageCircle size={16} className="text-maat-gold" />
                  <span>{t.about.languages}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-xl border border-maat-sand text-maat-blue text-sm font-medium">
                  <Cpu size={16} className="text-maat-gold" />
                  <span>{t.about.certification}</span>
                </div>
                {t.about.tags.map((tag, i) => {
                  const TagIcon = tagIcons[i] ?? Cpu;
                  return (
                    <div key={tag} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-xl border border-maat-sand text-maat-blue/80 text-sm">
                      <TagIcon size={16} className="text-maat-gold" />
                      <span>{tag}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Timeline Section */}
      <section className="py-24 bg-maat-stone border-y border-maat-sand">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-maat-blue mb-4">{t.experience.title}</h2>
            <p className="text-maat-blue/60 max-w-2xl mx-auto">{t.experience.subtitle}</p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Comprehensive CV-based Timeline items */}
            <div className="relative pl-8 border-l-2 border-maat-sand space-y-16">
              {[
                {
                  period: '2025–2026',
                  role: 'Software Architect',
                  company: 'Lateral',
                  subItems: [
                    { label: 'Tattoo Partners | Project Recovery & Feature Building', period: '2025–2026' },
                    { label: 'Leading Real-estate company | Rent management app', period: '2025' },
                    { label: 'MAJC (US Startup) | Workforce platform', period: '2025' }
                  ]
                },
                {
                  period: '2022–2025',
                  role: 'Software Architect',
                  company: 'Itho Daalderop',
                  subItems: [
                    { label: 'Cloud Platform Migration & Impact Analysis', period: '2024–2025' },
                    { label: 'IoT Platform Redesign & Wi-Fi Project Lead', period: '2022–2024' },
                    { label: 'IDesign methodology implementation', period: '2022–2025' }
                  ]
                },
                {
                  period: '2020–2024',
                  role: 'Software Architect',
                  company: 'ilionx',
                  subItems: [
                    { label: 'Sustainability Propositions', period: '2024' },
                    { label: 'Eventplatform (Ironman, Limburgs Mooiste)', period: '2020' },
                    { label: 'Gewoon Geregeld | Service platform', period: '2020' }
                  ]
                },
                {
                  period: '2017–2022',
                  role: 'Senior Software Engineer',
                  company: 'ilionx / Various',
                  subItems: [
                    { label: 'BDO | Architecture & Implementation', period: '2020–2022' },
                    { label: 'APG | B2B Customer 360 & Dynamics CRM', period: '2018–2020' },
                    { label: 'Telfort Zakelijk | Servi2 & API Provisioning Lead', period: '2017–2018' },
                    { label: 'Gewoon Geregeld | Microservices & ARM', period: '2018' }
                  ]
                },
                {
                  period: '2012–2017',
                  role: 'Software Engineer',
                  company: 'KEMBIT / Various',
                  subItems: [
                    { label: 'Canon Océ | Real-time React Dashboard', period: '2017' },
                    { label: 'Medtronic | European Tax Engine Lead', period: '2015–2016' },
                    { label: 'High Tech Campus Eindhoven | Access Portal', period: '2013–2014' },
                    { label: 'Schiphol | Lost & Found & Luggage Apps', period: '2012–2013' },
                    { label: 'MSM | Patient Dossier Web Application', period: '2012–2013' },
                    { label: 'Sitech Services & DSM | Manufacturing & Logisitcs', period: '2012–2014' }
                  ]
                },
              ].map((item, idx) => (
                <div key={idx} className="relative group">
                  {/* Marker */}
                  <div className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-white border-4 border-maat-gold group-hover:scale-125 transition-transform duration-300"></div>

                  <div className="flex flex-col md:flex-row md:items-baseline md:gap-4 mb-2">
                    <span className="text-xl font-bold text-maat-gold uppercase tracking-widest whitespace-nowrap">
                      {item.period}
                    </span>
                    <h3 className="text-xl font-serif text-maat-blue group-hover:text-maat-gold transition-colors">
                      {item.role}
                    </h3>
                  </div>

                  <div className="mb-4">
                    <p className="text-lg font-medium text-maat-blue/80">{item.company}</p>
                  </div>

                  {item.subItems && (
                    <div className="mt-4 space-y-2">
                      {item.subItems.map((sub, sIdx) => (
                        <div key={sIdx} className="flex items-baseline gap-3 group/sub">
                          <div className="w-1.5 h-1.5 rounded-full bg-maat-gold/30 group-hover/sub:bg-maat-gold transition-colors flex-shrink-0 relative top-[-2px]"></div>
                          <p className="text-sm text-maat-blue/70 leading-relaxed">
                            <span className="font-medium text-maat-blue">{sub.label}</span>
                            <span className="mx-2 text-maat-sand">|</span>
                            <span className="text-[11px] font-bold text-maat-gold/80 uppercase tracking-widest">{sub.period}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career & Skills */}
      <section className="py-24 bg-maat-blue text-maat-stone">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.about.expertiseTitle}</h2>
            <div className="w-20 h-1 bg-maat-gold mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 p-10 rounded-4xl border border-white/10">
              <BookOpen className="text-maat-gold mb-6" size={40} strokeWidth={1.5} />
              <h3 className="text-xl font-serif mb-4">{t.about.skill1.title}</h3>
              <p className="text-maat-stone/60 leading-relaxed">{t.about.skill1.desc}</p>
            </div>
            <div className="bg-white/5 p-10 rounded-4xl border border-white/10">
              <Cpu className="text-maat-gold mb-6" size={40} strokeWidth={1.5} />
              <h3 className="text-xl font-serif mb-4">{t.about.skill2.title}</h3>
              <p className="text-maat-stone/60 leading-relaxed">{t.about.skill2.desc}</p>
            </div>
            <div className="bg-white/5 p-10 rounded-4xl border border-white/10">
              <Users className="text-maat-gold mb-6" size={40} strokeWidth={1.5} />
              <h3 className="text-xl font-serif mb-4">{t.about.skill3.title}</h3>
              <p className="text-maat-stone/60 leading-relaxed">{t.about.skill3.desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Contact Push */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-serif text-maat-blue mb-8">{t.about.ctaTitle}</h2>
          <p className="text-lg text-maat-blue/70 mb-12">
            {t.about.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to="/contact" className="bg-maat-blue text-maat-stone px-12 py-4 rounded-3xl font-bold flex items-center hover:bg-maat-gold hover:text-maat-blue transition-all shadow-xl">
              <Mail size={20} className="mr-3" /> {t.nav.contact}
            </Link>
            <a href="https://www.linkedin.com/in/bendegens/" target="_blank" rel="noopener noreferrer" className="bg-transparent border-2 border-maat-blue text-maat-blue px-12 py-4 rounded-3xl font-bold flex items-center hover:bg-maat-blue hover:text-maat-stone transition-all">
              <Linkedin size={20} className="mr-3" /> LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;